import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Users, Cpu, Plus, Trash2, Edit3, CheckCircle, XCircle, RefreshCw } from 'lucide-react'
import { useAlertStore } from '@/stores/stores'
import { AppShell, Topbar, Modal, ConfirmDialog, EmptyState, PageLoader, ToastContainer } from '../components/layout/Layout'
import { useForm } from 'react-hook-form'
import api from '../services/api'

const tenantsApi = {
    list:   ()         => api.get('/admin/tenants'),
    create: (data)     => api.post('/admin/tenants', data),
    update: (id, data) => api.put(`/admin/tenants/${id}`, data),
    delete: (id)       => api.delete(`/admin/tenants/${id}`),
}
const usersApi = {
    list:   ()         => api.get('/admin/users'),
    create: (data)     => api.post('/admin/users', data),
    update: (id, data) => api.put(`/admin/users/${id}`, data),
    delete: (id)       => api.delete(`/admin/users/${id}`),
}
const tenantsForSelect = () => api.get('/admin/tenants')
const systemApi = {
    info: () => api.get('/admin/system/info'),
}

export function TenantsPage() {
    const qc = useQueryClient()
    const { addToast } = useAlertStore()
    const [createOpen, setCreateOpen] = useState(false)
    const [editItem, setEditItem] = useState(null)
    const [deleteId, setDeleteId] = useState(null)
    const { register, handleSubmit, reset, setValue } = useForm()

    const { data: tenants = [], isLoading } = useQuery({
        queryKey: ['tenants'],
        queryFn: () => tenantsApi.list().then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []),
    })

    const openEdit = (t) => {
        setEditItem(t)
        setValue('name', t.name)
        setValue('slug', t.slug)
        setValue('email', t.email)
        setValue('max_screens', t.max_screens)
        setCreateOpen(true)
    }

    const save = async (data) => {
        try {
            editItem ? await tenantsApi.update(editItem.id, data) : await tenantsApi.create(data)
            qc.invalidateQueries(['tenants'])
            addToast({ type: 'success', title: editItem ? 'Tenant aktualizovan' : 'Tenant vytvoren' })
            setCreateOpen(false); setEditItem(null); reset()
        } catch (e) { addToast({ type: 'error', title: e.response?.data?.message ?? 'Chyba' }) }
    }

    const deleteTenant = async (id) => {
        try {
            await tenantsApi.delete(id)
            qc.invalidateQueries(['tenants'])
            addToast({ type: 'success', title: 'Tenant smazan' })
            setDeleteId(null)
        } catch (e) { addToast({ type: 'error', title: 'Chyba mazani' }) }
    }

    return (
        <AppShell>
            <Topbar title="Tenanti" subtitle="Sprava zakazniku platformy"
                actions={<button className="btn btn-primary btn-sm" onClick={() => { reset(); setEditItem(null); setCreateOpen(true) }}><Plus size={14} /> Novy tenant</button>}
            />
            <main className="app-main">
                {isLoading ? <PageLoader /> : tenants.length > 0 ? (
                    <div className="card">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead><tr><th>Nazev</th><th>Slug</th><th>E-mail</th><th>Obrazovky</th><th>Stav</th><th></th></tr></thead>
                                <tbody>
                                    {tenants.map(t => (
                                        <tr key={t.id}>
                                            <td style={{ fontWeight: 500 }}>{t.name}</td>
                                            <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>{t.slug}</td>
                                            <td>{t.email}</td>
                                            <td>{t.screens_count ?? 0} / {t.max_screens ?? 'neomezeno'}</td>
                                            <td>{t.active
                                                ? <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} />Aktivni</span>
                                                : <span style={{ color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 4 }}><XCircle size={14} />Neaktivni</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(t)}><Edit3 size={14} /></button>
                                                    <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--red)' }} onClick={() => setDeleteId(t.id)}><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <EmptyState icon={Building2} title="Zadni tenanti" text="Vytvorte prvniho zakaznika platformy"
                        action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Vytvorit tenanta</button>}
                    />
                )}
            </main>
            <Modal open={createOpen} onClose={() => { setCreateOpen(false); setEditItem(null); reset() }}
                title={editItem ? 'Upravit tenanta' : 'Novy tenant'}
                footer={<><button className="btn btn-secondary" onClick={() => { setCreateOpen(false); reset() }}>Zrusit</button><button className="btn btn-primary" onClick={handleSubmit(save)}>{editItem ? 'Ulozit' : 'Vytvorit'}</button></>}>
                <div className="form-group"><label className="form-label">Nazev</label><input {...register('name', { required: true })} className="form-input" placeholder="Firma s.r.o." autoFocus /></div>
                <div className="form-group"><label className="form-label">Slug</label><input {...register('slug', { required: true })} className="form-input" placeholder="firma-sro" /></div>
                <div className="form-group"><label className="form-label">E-mail</label><input {...register('email')} className="form-input" type="email" placeholder="admin@firma.cz" /></div>
                <div className="form-group"><label className="form-label">Max. obrazovek</label><input {...register('max_screens')} className="form-input" type="number" placeholder="10" /></div>
            </Modal>
            <ConfirmDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteTenant(deleteId)} title="Smazat tenanta" message="Tenant a vsechna jeho data budou trvale smazana." danger />
            <ToastContainer />
        </AppShell>
    )
}

export function UsersPage() {
    const qc = useQueryClient()
    const { addToast } = useAlertStore()
    const [createOpen, setCreateOpen] = useState(false)
    const [editItem, setEditItem] = useState(null)
    const [deleteId, setDeleteId] = useState(null)
    const { register, handleSubmit, reset, setValue } = useForm()

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: () => usersApi.list().then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []),
    })
    const { data: tenants = [] } = useQuery({
        queryKey: ['tenants-select'],
        queryFn: () => tenantsForSelect().then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []),
    })

    const openEdit = (u) => {
        setEditItem(u)
        setValue('name', u.name)
        setValue('email', u.email)
        setValue('role', u.role)
        setValue('tenant_id', u.tenant_id ?? '')
        setCreateOpen(true)
    }

    const save = async (data) => {
        try {
            editItem ? await usersApi.update(editItem.id, data) : await usersApi.create(data)
            qc.invalidateQueries(['users'])
            addToast({ type: 'success', title: editItem ? 'Uzivatel aktualizovan' : 'Uzivatel vytvoren' })
            setCreateOpen(false); setEditItem(null); reset()
        } catch (e) { addToast({ type: 'error', title: e.response?.data?.message ?? 'Chyba' }) }
    }

    const deleteUser = async (id) => {
        try {
            await usersApi.delete(id)
            qc.invalidateQueries(['users'])
            addToast({ type: 'success', title: 'Uzivatel smazan' })
            setDeleteId(null)
        } catch (e) { addToast({ type: 'error', title: 'Chyba mazani' }) }
    }

    return (
        <AppShell>
            <Topbar title="Uzivatele" subtitle="Sprava uzivatelu platformy"
                actions={<button className="btn btn-primary btn-sm" onClick={() => { reset(); setEditItem(null); setCreateOpen(true) }}><Plus size={14} /> Novy uzivatel</button>}
            />
            <main className="app-main">
                {isLoading ? <PageLoader /> : users.length > 0 ? (
                    <div className="card">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead><tr><th>Jmeno</th><th>E-mail</th><th>Role</th><th>Stav</th><th></th></tr></thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u.id}>
                                            <td style={{ fontWeight: 500 }}>{u.name}</td>
                                            <td style={{ color: 'var(--text-muted)' }}>{u.email}</td>
                                            <td><span className="badge badge-info">{u.role}</span></td>
                                            <td>{u.active
                                                ? <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} />Aktivni</span>
                                                : <span style={{ color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 4 }}><XCircle size={14} />Neaktivni</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(u)}><Edit3 size={14} /></button>
                                                    <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--red)' }} onClick={() => setDeleteId(u.id)}><Trash2 size={14} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <EmptyState icon={Users} title="Zadni uzivatele" text="Vytvorte prvniho uzivatele platformy"
                        action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Vytvorit uzivatele</button>}
                    />
                )}
            </main>
            <Modal open={createOpen} onClose={() => { setCreateOpen(false); setEditItem(null); reset() }}
                title={editItem ? 'Upravit uzivatele' : 'Novy uzivatel'}
                footer={<><button className="btn btn-secondary" onClick={() => { setCreateOpen(false); reset() }}>Zrusit</button><button className="btn btn-primary" onClick={handleSubmit(save)}>{editItem ? 'Ulozit' : 'Vytvorit'}</button></>}>
                <div className="form-group"><label className="form-label">Jmeno</label><input {...register('name', { required: true })} className="form-input" placeholder="Jan Novak" autoFocus /></div>
                <div className="form-group"><label className="form-label">E-mail</label><input {...register('email', { required: true })} className="form-input" type="email" placeholder="jan@firma.cz" /></div>
                {!editItem && <div className="form-group"><label className="form-label">Heslo</label><input {...register('password', { required: !editItem })} className="form-input" type="password" placeholder="Minimalne 8 znaku" /></div>}
                <div className="form-group">
                    <label className="form-label">Role</label>
                    <select {...register('role')} className="form-select">
                        <option value="user">Uzivatel</option>
                        <option value="admin">Admin</option>
                        <option value="superadmin">Superadmin</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="form-label">Tenant (volitelne)</label>
                    <select {...register('tenant_id')} className="form-select">
                        <option value="">-- Bez tenanta (superadmin) --</option>
                        {tenants.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
            </Modal>
            <ConfirmDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteUser(deleteId)} title="Smazat uzivatele" message="Uzivatel bude trvale smazan." danger />
            <ToastContainer />
        </AppShell>
    )
}

export function SystemPage() {
    const { addToast } = useAlertStore()
    const { data: info, isLoading, refetch } = useQuery({
        queryKey: ['system-info'],
        queryFn: () => systemApi.info().then(r => r.data).catch(() => ({})),
        refetchInterval: 30000,
    })

    return (
        <AppShell>
            <Topbar title="System" subtitle="Stav serverovych sluzeb"
                actions={<button className="btn btn-secondary btn-sm" onClick={() => refetch()}><RefreshCw size={14} /> Obnovit</button>}
            />
            <main className="app-main">
                {isLoading ? <PageLoader /> : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div className="card">
                            <div className="card-title" style={{ marginBottom: 16 }}>Systemove informace</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    ['PHP verze', info?.php_version ?? 'PHP 8.3'],
                                    ['Laravel', info?.laravel_version ?? '11.x'],
                                    ['Disk volny', info?.disk_free ?? '—'],
                                    ['RAM vyuziti', info?.ram_usage ?? '—'],
                                    ['Uptime', info?.uptime ?? '—'],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{label}</span>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>{value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-title" style={{ marginBottom: 16 }}>Informace o platforme</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Verze</span>
                                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>{info?.app_version ?? '1.0.0'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Prostredi</span>
                                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>production</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <ToastContainer />
        </AppShell>
    )
}
